import { AnimatePresence } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { TimersContext, TwitchChannelContext } from "../../contexts/index.js";
import useEbsApiClient from "../../hooks/useEbsApiClient.js";
import useFollowerRaffleLogic, {
  FOLLOWER_STATUSES,
} from "../../hooks/useFollowerRaffleLogic.js";
import useFollowersLogic from "../../hooks/useFollowersLogic.js";
import useMessages from "../../hooks/useMessages.js";
import usePrevious from "../../hooks/usePrevious.js";
import Flyout from "../Flyout/Flyout.js";
import FollowerBuilder from "../FollowerBuilder/FollowerBuilder.js";
import FollowerGallery from "../FollowerGallery/FollowerGallery.js";
import HelpHinder from "../HelpHinder/HelpHinder.js";
import OverlayMessagesContainer from "../OverlayMessagesContainer/OverlayMessagesContainer.js";
import Raffle from "../Raffle/Raffle.js";
import SafeZones from "../SafeZones/SafeZones.js";

import "./VideoOverlay.scss";

const FollowerGalleryActivationButton = ({ isForcedVisible, ...props }) => {
  return (
    <button
      {...props}
      className={classNames("video-overlay_follower-gallery-button", {
        "video-overlay_follower-gallery-button--force-visible": isForcedVisible,
      })}
    >
      <img src="assets/ui/icons/follower-icon.png" alt="" />
    </button>
  );
};

const VideoOverlay = () => {
  const {
    activeRaffle,
    enterRaffle,
    dismissRaffle,
    permanentlyDismissRaffle,
    follower,
    createFollower,
    premiumBitsCost,
  } = useFollowerRaffleLogic();
  const {
    followers,
    refetchFollowers,
    activeSelectionEvent,
    selectedFollowerForSelectionEvent,
    selectFollowerForSelectionEvent,
  } = useFollowersLogic();
  const previousActiveSelectionEvent = usePrevious(activeSelectionEvent);
  const [channelInfo, setChannelInfo] = useState(null);
  const [timers, setTimers] = useState(null);
  const ebsApiClient = useEbsApiClient();
  const { i18n } = useTranslation();
  const [isFollowerGalleryOpen, setIsFollowerGalleryOpen] = useState(false);
  const [
    isFollowerGalleryButtonForcedVisible,
    setIsFollowerGalleryButtonForcedVisible,
  ] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: channelData }, { data: timersData }] = await Promise.all([
        ebsApiClient.get("/channel"),
        ebsApiClient.get("/timers"),
      ]);
      i18n.changeLanguage(channelData.language || "en");
      setChannelInfo(channelData);
      setTimers(timersData);

      setTimeout(() => {
        setIsFollowerGalleryButtonForcedVisible(true);

        setTimeout(() => {
          setIsFollowerGalleryButtonForcedVisible(false);
        }, 5000);
      }, 1000);
    })();
  }, []);

  useEffect(() => {
    if (!previousActiveSelectionEvent && activeSelectionEvent) {
      refetchFollowers();
      setIsFollowerGalleryOpen(true);
    }

    if (previousActiveSelectionEvent && !activeSelectionEvent) {
      setIsFollowerGalleryOpen(false);
    }
  }, [previousActiveSelectionEvent, activeSelectionEvent]);

  const handleClickFollowerGalleryButton = () => {
    setIsFollowerGalleryOpen(true);
    refetchFollowers();
  };

  if (!channelInfo || !timers) {
    return null;
  }

  return (
    <TwitchChannelContext.Provider value={channelInfo}>
      <TimersContext.Provider value={timers}>
        <div className="video-overlay">
          <SafeZones />
          <HelpHinder />
          <OverlayMessagesContainer />
          {!isFollowerGalleryOpen && (
            <FollowerGalleryActivationButton
              isForcedVisible={isFollowerGalleryButtonForcedVisible}
              onClick={handleClickFollowerGalleryButton}
            />
          )}
          <AnimatePresence>
            {isFollowerGalleryOpen && (
              <Flyout
                direction="left"
                canBeMinimized={false}
                canBeClosed={true}
                onClose={() => setIsFollowerGalleryOpen(false)}
              >
                <FollowerGallery
                  followers={followers}
                  selectedFollower={selectedFollowerForSelectionEvent}
                  activeSelectionEvent={activeSelectionEvent}
                  onSelectFollower={selectFollowerForSelectionEvent}
                />
              </Flyout>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {Boolean(activeRaffle) && (
              <Raffle
                activeRaffle={activeRaffle}
                hasFollower={Boolean(follower)}
                onDismissRaffle={dismissRaffle}
                onPermanentlyDismissRaffle={permanentlyDismissRaffle}
                onEnterRaffle={enterRaffle}
              />
            )}
          </AnimatePresence>
          {Boolean(
            follower && follower.status === FOLLOWER_STATUSES.READY_FOR_CREATION
          ) && (
            <Flyout direction="left">
              <FollowerBuilder
                premiumBitsCost={premiumBitsCost}
                onFinishBuilding={createFollower}
              />
            </Flyout>
          )}
        </div>
      </TimersContext.Provider>
    </TwitchChannelContext.Provider>
  );
};

export default VideoOverlay;
